import Link from "components/common/LinkButton";
import swaggerImageImg1 from "static/images/get-started/developers-section/image1.png";
import Image2 from "static/images/get-started/developers-section/image2.png";
import Image3 from "static/images/get-started/developers-section/image3.png";
import Image4 from "static/images/get-started/developers-section/image4.png";
import Image5 from "static/images/get-started/developers-section/image5.png";
import Image6 from "static/images/get-started/developers-section/image6.png";
import Image7 from "static/images/get-started/developers-section/image7.png";
import Image8 from "static/images/get-started/developers-section/image8.png";
import Image9 from "static/images/get-started/developers-section/image9.png";
import { H1, H2, H3, H4, H5 } from "../Headings";
import UrlHighlighter from "../UrlHighlighter";
import Highlighter from "../Highlighter";
import PromineoJsonViewer from "components/common/PromineoJsonViewer";
import { useRef } from "react";
import { useDispatch } from "react-redux";
import { setSelectedIndex } from "store/slices/DocsSlice";
import { DocsTab } from "shared/enums/DocsTabEnum";
import { useHistory } from "react-router";
import ExternalLink from "../ExternalLink";
import * as routePath from "shared/constants/RoutePathConstants";

export default function DevelopersSectionContent() {
  const dispatch = useDispatch();
  const history = useHistory();

  const howTimePhasingDoneRef = useRef(null);
  const bodyExplainedRef = useRef(null);
  const usingCalendarRef = useRef(null);
  const usingTimePhasingUtilityRef = useRef(null);

  const scrollTo = (ref: any) => {
    ref.current.scrollIntoView();
  };

  const handleHowItWorksClick = () => {
    dispatch(setSelectedIndex(DocsTab.HowItWorks));
  };

  const handleBodyExplainedClick = () => {
    scrollTo(bodyExplainedRef);
  };

  const handleGotoHowTimePhasingDoneClick = () => {
    scrollTo(howTimePhasingDoneRef);
  };

  const handleGotoUsingTimePhasingUtilityClick = () => {
    scrollTo(usingTimePhasingUtilityRef);
  };

  const handleGotoUsingCalendarClick = () => {
    scrollTo(usingCalendarRef);
  };

  const handleGotoTokensClick = () => {
    history.push(routePath.TOKENS);
  };

  const swaggerDocumentationLink =
    "https://timephasing.promineo.com/swagger/index.html";
  const timePhasingServiceApiLink = "https://timephasing.promineo.com/";

  const SwaggerLink = () => {
    return <ExternalLink url={swaggerDocumentationLink} />;
  };

  return (
    <div className="text-base">
      <div>
        <H1>Developers Section</H1>
        <p>
          To understand how the service actually time-phases data, read the{" "}
          <Link
            onClick={handleGotoHowTimePhasingDoneClick}
            className="text-primaryRed"
          >
            How is time-phasing done
          </Link>{" "}
          section in this page.
        </p>
        <p>
          We also recommend you to run our feature demo in the{" "}
          <Link onClick={handleHowItWorksClick} className="text-primaryRed">
            How it works
          </Link>{" "}
          page to get familiarized with the request and response content.
        </p>
      </div>
      <div className="mt-6 space-y-4">
        <div>
          <H2>URL</H2>
          <div className="mb-4">
            <p>Base URL for the timePhasing service is:</p>
            <ExternalLink url={timePhasingServiceApiLink} />
          </div>
          <div>
            <p>For detailed documentation, schema, and examples, enter:</p>
            <ExternalLink url={swaggerDocumentationLink} />
          </div>
        </div>

        <div>
          <H2>Endpoints</H2>
          Use the <Highlighter>/timephase</Highlighter> route to post your data
          and get time-phased data in return
        </div>

        <div>
          <H2>Methods</H2>
          <p className="mb-4">
            Use the POST method to send a request and get time-phased data in
            return
          </p>
          <Highlighter isMultiLine>
            <div>
              <UrlHighlighter
                method="POST"
                link="https://timephasing.promineo.com/timephase HTTP/1.1"
              />
            </div>
          </Highlighter>
        </div>

        <div>
          <H2>Authorization</H2>
          <p>
            For authorization, the API requires you to provide a bearer token in
            the header of your POST.
          </p>
          To create a token key, enter the{" "}
          <Link onClick={handleGotoTokensClick} className="text-primaryRed">
            Tokens
          </Link>{" "}
          page.
        </div>
      </div>
      <div className="mt-4">
        <H3>Parameters (headers / body)</H3>
        <div className="mt-6 space-y-4">
          <div>
            <H4>Headers</H4>
            <div className="mb-4">
              Provide the following headers (replace YOUR_KEY_HERE with your own
              token key generated):
            </div>
            <Highlighter isMultiLine>
              <div>
                <div>
                  Content-Type:{" "}
                  <span className="text-promRed2 ml-1">"application/json"</span>
                </div>
                <div>
                  Accept:{" "}
                  <span className="text-promRed2 ml-1">"application/json"</span>
                </div>
                <div>
                  Authorization:{" "}
                  <span className="text-promRed2 ml-1">
                    "bearer YOUR_KEY_HERE"
                  </span>
                </div>
              </div>
            </Highlighter>
          </div>
          <div>
            <H4>Body</H4>
            The content of the BODY should (in JSON format) must contain the
            following:
            <div className="mt-4 space-y-4">
              <div>
                <div>Required content: </div>
                <div>
                  <Highlighter>Activities</Highlighter> - your input data
                </div>
                <div>
                  <Highlighter>FieldMappings</Highlighter> - information about
                  your data
                </div>
                <div>
                  <Highlighter>Outputs</Highlighter> - how you want data
                  returned from service
                </div>
              </div>

              <div>
                <div>Optional content: </div>
                <div>
                  <Highlighter>Calendars</Highlighter> - If any of your
                  activities are using calendars
                </div>
                <div>
                  <Highlighter>Profiles</Highlighter> - If any of your
                  activities should be time-phased according to predefined
                  profiles
                </div>
                <div>
                  <Highlighter>PreProcessor</Highlighter> - If you want to
                  change default settings/behavior of service
                </div>
              </div>
              <div className="mt-4">
                <Highlighter isMultiLine>
                  <PromineoJsonViewer
                    src={{
                      Activities: [],
                      FieldMappings: {},
                      Outputs: [],
                      Calendars: [],
                      Profiles: [],
                      PreProcessor: {},
                    }}
                  ></PromineoJsonViewer>
                </Highlighter>
              </div>
              <div className="mt-4">
                <p>
                  To get a more comprehensive description of BODY content, read
                  the{" "}
                  <Link
                    onClick={handleBodyExplainedClick}
                    className="text-primaryRed"
                  >
                    Body explained
                  </Link>{" "}
                  in this page.
                </p>
                <p>
                  To see entire schema for request body, go to <SwaggerLink />{" "}
                  (as shown below) :
                </p>
                <img src={swaggerImageImg1} alt="swagger" className="mt-4" />
              </div>
            </div>
          </div>
          <div>
            <H4>Request example</H4>
            Included in swagger :{" "}
            <Highlighter>{swaggerDocumentationLink}</Highlighter>
          </div>
          <div>
            <H4>Response example</H4>
            Included in swagger :{" "}
            <Highlighter>{swaggerDocumentationLink}</Highlighter>
          </div>
          <div>
            <H4>Error handling</H4>
            Included in swagger :{" "}
            <Highlighter>{swaggerDocumentationLink}</Highlighter>
          </div>
        </div>
      </div>
      <div className="mt-4" ref={bodyExplainedRef}>
        <H2>Body explained</H2>
        <div className="font-bold mb-4">Required content of BODY:</div>
        <H3>Activities</H3>
        <div className="mb-4">
          <p>
            Provide your input data as an array of{" "}
            <Highlighter>Activities</Highlighter> (can be schedule activities,
            budget items, or other items to be time-phased)
          </p>
          <p>
            The properties of each activity can be named according to your own
            preferences.
          </p>
          <p>
            {" "}
            However, the timePhasing service requires this information to be
            included:
          </p>
        </div>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <span className="font-bold">start</span>
            <span>
              {" "}
              - property providing start date of time-range (if missing, finish
              date is used)
            </span>
          </li>
          <li>
            <span className="font-bold">finish </span>
            <span>
              {" "}
              - property providing finish date of time-range (if missing, start
              date is used)
            </span>
          </li>
          <li>
            <span className="font-bold">value </span>
            <span> - property providing the value to be time-phased</span>
          </li>
        </ul>
        <p className="mb-4">
          In addition, if input data also uses calendars and/or profiles, or you
          want to group the result on any field(s), this information should be
          added to the activities list
        </p>
        <ul className="list-disc font-bold pl-8 mb-4">
          <li>profile id</li>
          <li>calendar id</li>
          <li>group field 1, group field 2, ...</li>
        </ul>
        <p className="mb-4">
          An example of <Highlighter>Activities</Highlighter> JSON here:
        </p>
        <div className="mb-4">
          <Highlighter isMultiLine>
            <div>
              <PromineoJsonViewer
                src={{
                  Activities: [
                    {
                      ActivityId: "EL001",
                      EarlyStart: "2021-03-01T00:00:00",
                      EarlyFinish: "2021-06-01T00:00:00",
                      Manhours: 98.4,
                      Calendar: "RegularOffice",
                      Profile: "NormalCenter",
                      Discipline: "Piping",
                    },
                    {
                      ActivityId: "KN001",
                      EarlyStart: "2021-05-01T00:00:00",
                      EarlyFinish: "2021-10-01T00:00:00",
                      Manhours: 652.5,
                      Calendar: "RotationAllDays",
                      Profile: "EndLoad",
                      Discipline: "Structural",
                    },
                  ],
                }}
              ></PromineoJsonViewer>
            </div>
          </Highlighter>
        </div>
        <H3>FieldMappings</H3>
        <div className="mb-4">
          <p>
            As already mentioned, the timePhasing service requires you to
            provide information about your data.
          </p>
          <p>
            The <Highlighter>FieldMappings</Highlighter> allows you to provide a
            mapping between your input data and required properties of the
            service.
          </p>
        </div>
        <div className="mb-4">Required content:</div>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <span className="font-bold">StartDateField </span>
            <span>
              {" "}
              - Name of field containing start date in{" "}
              <Highlighter>Activities</Highlighter> list
            </span>
          </li>
          <li>
            <span className="font-bold">FinishDateField </span>
            <span>
              {" "}
              - Name of field containing finish date in{" "}
              <Highlighter>Activities</Highlighter> list
            </span>
          </li>
          <li>
            <span className="font-bold">ValueField.Field </span>
            <span>
              {" "}
              - Name of field containing value to time-phase in{" "}
              <Highlighter>Activities</Highlighter> list
            </span>
          </li>
          <li>
            <span className="font-bold">ValueField.Type </span>
            <span> - Enumerator; Either Qty (Quantity) or Load</span>
          </li>
        </ul>
        <span>Optional content:</span>
        <p className="mb-4">
          If you want the timephasing service to use calendars and/or profiles,
          <Highlighter>Calendars</Highlighter> and{" "}
          <Highlighter>Profiles</Highlighter> must be included in BODY as well
          as defined mapping of fields provided in{" "}
          <Highlighter>Activities</Highlighter>
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <span className="font-bold">CalendarKeyField </span>
            <span>
              {" "}
              - Name of field containing calendar key in{" "}
              <Highlighter>Activities</Highlighter> list
            </span>
          </li>
          <li>
            <span className="font-bold">ProfileKeyField </span>
            <span>
              {" "}
              - Name of field containing profile key in{" "}
              <Highlighter>Activities</Highlighter> list
            </span>
          </li>
        </ul>
        <p className="mb-4">
          An example of <Highlighter>FieldMappings</Highlighter> here:
        </p>
        <div className="mb-4">
          <Highlighter isMultiLine>
            <PromineoJsonViewer
              src={{
                FieldMappings: {
                  StartDateField: "EarlyStart",
                  FinishDateField: "EarlyFinish",
                  ValueField: {
                    Type: "Qty",
                    Field: "Manhours",
                  },
                  CalendarKeyField: "Calendar",
                  ProfileKeyField: "Profile",
                },
              }}
            ></PromineoJsonViewer>
          </Highlighter>
        </div>
        <div className="mb-4">
          <H3>Outputs</H3>
          <div className="mb-4">
            <p>
              The <Highlighter>Outputs</Highlighter> allows you to tell the
              timePhasing service how the returned time-phased data should be
              shaped.
            </p>
            Specify one or more Outputs to be delivered by the service.
          </div>
          <div className="mb-4">Title: Name of the array of output values.</div>
          <div className="mb-4">
            An example of <Highlighter>Outputs</Highlighter> JSON here:
          </div>
          <div className="mb-4">
            <Highlighter isMultiLine>
              <PromineoJsonViewer
                src={{
                  Outputs: [
                    {
                      Title: "myTimephasedData",
                      PointsInTime: {
                        Title: "Period",
                        Type: "Week",
                        FirstDayOfWeek: "Sunday",
                      },
                      Values: [
                        {
                          Title: "Qty",
                          Type: "PerQty",
                        },
                      ],
                      GroupBy: [
                        {
                          Title: "Group",
                          Type: "Discipline",
                        },
                      ],
                    },
                  ],
                }}
              ></PromineoJsonViewer>
            </Highlighter>
          </div>
          <div className="mb-4">
            <H4>Different "PointsInTime" (granularities of periods)</H4>
            <p>
              Periods returned by the timePhasing service is specified using the
              <Highlighter>PoinstInTime</Highlighter> property.
            </p>
            <p>
              Multiple types of <Highlighter>PoinstInTime</Highlighter> are
              available in the service.
            </p>
          </div>
          <div className="mt-6 mb-4">
            <div className="mb-6">
              <H5>Standard types</H5>
              <div>
                <p>
                  Standard types provided are <Highlighter>Day</Highlighter>,{" "}
                  <Highlighter>Week</Highlighter>,{" "}
                  <Highlighter>Month</Highlighter>,{" "}
                  <Highlighter>Quarter</Highlighter>, and{" "}
                  <Highlighter>Year</Highlighter>.
                </p>
                <p>Period returned will be the end of each period.</p>
                <p>
                  For example: If you provide the type{" "}
                  <Highlighter>Month</Highlighter>, the returned period for
                  March 2021 will be{" "}
                  <Highlighter>"2021-10-31T23:59:59"</Highlighter>.
                </p>
                Note: If you choose <Highlighter>Week</Highlighter>, the
                <Highlighter>FirstDayOfWeek</Highlighter>
                must be provided (enumerator; Sunday, Monday, ..., Saturday).
              </div>
            </div>
            <div className="mb-6">
              <H5>Special types</H5>
              <div>
                <div className="mt-4">
                  <H5>CRON</H5>
                </div>
                <p className="mb-4">
                  CRON allows you to define points in time based on a Cron
                  Expression (see{" "}
                  <ExternalLink url="http://www.cronmaker.com/" /> )
                  <p>
                    For example, the following will give the 2nd Sunday of Every
                    month:
                  </p>
                </p>
                <Highlighter isMultiLine>
                  <PromineoJsonViewer
                    src={{
                      PointsInTime: {
                        Title: "Period",
                        Type: "CRON",
                        CronExpression: "0 0 12 ? 1/1 SUN#2 *",
                      },
                    }}
                  ></PromineoJsonViewer>
                </Highlighter>
              </div>
              <div className="mt-4">
                <H5>Custom</H5>
              </div>
              <p className="mb-4">
                <p>
                  <Highlighter>Custom</Highlighter> allows you to define each
                  individual point in time by providing a set of dates.
                </p>{" "}
                An example here:
              </p>
              <Highlighter isMultiLine>
                <PromineoJsonViewer
                  src={{
                    PointsInTime: {
                      Title: "Period",
                      Type: "Custom",
                      CustomPointsInTime: [
                        "2021-03-15T00:00:00",
                        "2021-06-20T00:00:00",
                        "2021-10-10T00:00:00",
                      ],
                    },
                  }}
                ></PromineoJsonViewer>
              </Highlighter>
            </div>
          </div>
        </div>
        <div className="mb-4">
          <div className="font-bold mb-6">Optional content of BODY:</div>
          <div className="space-y-6">
            <div>
              <H3>Calendars</H3>
              <div className="space-y-4 mb-4">
                <p>
                  If you want calendars applied to the time-phasing algorithm,
                  an array of <Highlighter>Calendars</Highlighter> being
                  referred to from <Highlighter>Activities</Highlighter> must be
                  included in the BODY.
                </p>
                <p>
                  Specify an <Highlighter>Id</Highlighter> and an array of{" "}
                  <Highlighter>CalendarOperations</Highlighter> for each
                  calendar being used by <Highlighter>Activities</Highlighter>.
                </p>
                <p>
                  Each <Highlighter>CalendarOperation</Highlighter> must be
                  given a type (enumerator; either
                  <Highlighter>AddWorkingTime</Highlighter> or{" "}
                  <Highlighter>RemoveWorkingTime</Highlighter>) and an array of
                  defined periods. 4 different types of periodic definitions are
                  supported:
                </p>
              </div>
              <ul className="list-disc pl-8 mb-4">
                <li>
                  <Highlighter>WeeklyRepeatingPeriods</Highlighter> - Specify
                  number of hours per weekday
                </li>
                <li>
                  <Highlighter>ConnectedPeriods</Highlighter> - Specify timespan
                  (only applicable for type{" "}
                  <Highlighter>RemoveWorkingTime</Highlighter>)
                </li>
                <li>
                  <Highlighter>CronRepeatingPeriods</Highlighter> - Specify CRON
                  expression and number of hours
                </li>
                <li>
                  <Highlighter>RotationRepeatingPeriods</Highlighter> - Specify
                  start, pattern and number of hours
                </li>
              </ul>
              <div className="mb-4">
                <p>
                  Details of each property provided in
                  <Highlighter>{swaggerDocumentationLink}</Highlighter>
                </p>
                <p>
                  To learn how calendars are being applied when time-phasing,
                  see{" "}
                  <Link
                    onClick={handleGotoUsingCalendarClick}
                    className="text-primaryRed"
                  >
                    Using Calendar
                  </Link>{" "}
                  in the{" "}
                  <Link
                    onClick={handleGotoHowTimePhasingDoneClick}
                    className="text-primaryRed"
                  >
                    How is time-phasing done
                  </Link>{" "}
                  section
                </p>
              </div>
              <div>
                <span className="font-bold">NOTE</span>: If you are using
                Primavera P6 or Safran, we provide a{" "}
                <Link
                  onClick={handleGotoUsingTimePhasingUtilityClick}
                  className="text-primaryRed"
                >
                  utility service
                </Link>{" "}
                to transform P6 and Safran calendars into a format understood by
                the timePhasing service.
              </div>
              <div></div>
            </div>
            <div>
              <H3>Profiles</H3>
              <div className="space-y-4 mb-4">
                <p>
                  If you want profiles applied to the time-phasing algorithm, an
                  array of <Highlighter>Profiles</Highlighter> being referred to
                  from <Highlighter>Activities</Highlighter> must be included in
                  the BODY.
                </p>
                <p>
                  Specify an <Highlighter>Id</Highlighter> and an array of{" "}
                  <Highlighter>Points</Highlighter> for each{" "}
                  <Highlighter>Profile</Highlighter> being used by{" "}
                  <Highlighter>Activities</Highlighter>.
                </p>
                <p>
                  Each <Highlighter>Point</Highlighter> in the{" "}
                  <Highlighter>Points</Highlighter> array has two properties{" "}
                  <Highlighter>{"{x,y}"}</Highlighter>
                </p>
                <ul className="list-disc pl-8 mb-4">
                  <li>
                    <Highlighter>x</Highlighter> - duration (integer between 0
                    and 100 representing 0-100% of the duration of the activity)
                  </li>
                  <li>
                    <Highlighter>y</Highlighter> - value (float representing how
                    much of the value of the activity to be distributed at the
                    time represented by x)
                  </li>
                </ul>
                <p>
                  NOTE: You can define as many points as you like, but the
                  "last" point should always be x=100, y=100
                </p>
                <p>Example of profiles:</p>
              </div>
              <Highlighter isMultiLine>
                <PromineoJsonViewer
                  src={{
                    Profiles: [
                      {
                        Id: "NormalCenter",
                        Points: [
                          { X: 10, Y: 2 },
                          { X: 20, Y: 6 },
                          { X: 30, Y: 14 },
                          { X: 40, Y: 28 },
                          { X: 50, Y: 50 },
                          { X: 60, Y: 72 },
                          { X: 70, Y: 86 },
                          { X: 80, Y: 94 },
                          { X: 90, Y: 98 },
                          { X: 100, Y: 100 },
                        ],
                      },
                      {
                        Id: "FrontLoad",
                        Points: [
                          { X: 0, Y: 0 },
                          { X: 10, Y: 10 },
                          { X: 30, Y: 50 },
                          { X: 50, Y: 72.5 },
                          { X: 90, Y: 95 },
                          { X: 100, Y: 100 },
                        ],
                      },
                      {
                        Id: "EndLoad",
                        Points: [
                          { X: 0, Y: 0 },
                          { X: 10, Y: 5 },
                          { X: 40, Y: 20 },
                          { X: 70, Y: 40 },
                          { X: 90, Y: 80 },
                          { X: 100, Y: 100 },
                        ],
                      },
                    ],
                  }}
                ></PromineoJsonViewer>
              </Highlighter>
            </div>
          </div>
        </div>
        <div ref={usingTimePhasingUtilityRef}>
          <H2>timePhasing Utility</H2>
          <div className="mb-4">
            <p>
              Having a hard time transforming your Primavera P6 or Safran
              calendars into a proper timePhasing calendar format?
            </p>
            <p>
              We are providing a utility service that helps you to do exactly
              this.
            </p>
          </div>
          <div className="space-y-4 mb-4">
            <div>
              <H3>Endpoints</H3>
              <p>
                Use the <Highlighter>/utility/p6/calendars</Highlighter> route
                to transform a Primavera Calendar into timePhasing format
              </p>
              <p>
                Use the <Highlighter>/utility/safran/calendarset</Highlighter>{" "}
                route to transform a Safran Calendar into timePhasing format
              </p>
            </div>
            <div>
              <H3>Primavera P6</H3>
              <div className="mb-4">
                <p>Read calendars from P6 API</p>
                <p>Convert received calendars from Xml to Json</p>
                <p>
                  Post the converted JSON as BODY to the utility service and the
                  service will return calendars transformed into timePhasing
                  calendars
                </p>
              </div>
              <Highlighter isMultiLine>
                <p>
                  <UrlHighlighter
                    method="POST"
                    link="https://timephasing.promineo.com/utility/p6/calendars HTTP/1.1"
                  />
                </p>
                <p>
                  Content-Type:{" "}
                  <span className="text-promRed2">"application/json"</span>{" "}
                </p>
                <p>
                  Accept:{" "}
                  <span className="text-promRed2">"application/json"</span>
                </p>
              </Highlighter>
            </div>
            <div>
              <H3>Safran</H3>
              <div className="mb-4">
                <p>Read calendars from Safran API</p>
                <p>
                  Post the received JSON as BODY to the utility service and the
                  service will return calendars transformed into timePhasing
                  calendars
                </p>
              </div>
              <Highlighter isMultiLine>
                <p>
                  <UrlHighlighter
                    method="POST"
                    link="https://timephasing.promineo.com/utility/safran/calendarset HTTP/1.1"
                  />
                </p>
                <p>
                  Content-Type:{" "}
                  <span className="text-promRed2">"application/json"</span>{" "}
                </p>
                <p>
                  Accept:{" "}
                  <span className="text-promRed2">"application/json"</span>
                </p>
              </Highlighter>
            </div>
          </div>
        </div>
        <div className="mt-4" ref={howTimePhasingDoneRef}>
          <H2>How is time-phasing done?</H2>
          <div>
            <div>
              <H3>Timephasing defined</H3>
              <div className="space-y-4">
                <div>
                  <p>
                    The name TimePhasing is a combination of{" "}
                    <ExternalLink
                      title="Time"
                      url="https://en.wikipedia.org/wiki/Time"
                    />{" "}
                    and{" "}
                    <ExternalLink
                      title="Phasing"
                      url="https://dictionary.cambridge.org/dictionary/english/phasing"
                    />{" "}
                    .
                  </p>
                  For our purpose, we use this definition of phasing from the{" "}
                  <ExternalLink
                    title="Cambridge dictionary"
                    url="https://dictionary.cambridge.org/dictionary/english/"
                  />{" "}
                  : "to introduce something in stages over a particular period
                  of time"
                </div>
                <p>Another way of saying this is :</p>
                <p>
                  "For a given item (1) with time-span (2) and quantity (3),
                  time-phasing will spread the given quantity over the days in
                  time-span"
                </p>
                <ul className="list-decimal px-8 space-y-1">
                  <li>
                    <span className="font-bold">Item</span> - Can be anything
                    (activity, position, budget item, beer, ...). Items will be
                    referred to as "activities" in this document
                  </li>
                  <li>
                    <span className="font-bold">Time-span</span> - Interval
                    defined by start and finish dates
                  </li>
                  <li>
                    <span className="font-bold">Quantity</span> - Can be any
                    unit (hours, cost, load, liters, tons, meters, ...).
                    Quantities will be referred to as "hours" in this document
                  </li>
                  <li>
                    <span className="font-bold">Points In Time</span> -
                    Time-unit of returned time-phasing result (days, weeks,
                    months, quarters, years, custom, CRON)
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-10">
              <H3>Understanding the concept</H3>
              <div className="mt-6 space-y-10">
                <div>
                  <H4>Basics</H4>
                  <p>
                    Activity with start/finish and hours sent to the timePhasing
                    service.
                  </p>
                  <p>
                    The service spread the hours equally over all days in
                    time-span:
                  </p>
                  <img src={Image2} className="mt-4" alt="timePhasing" />
                </div>
                <div ref={usingCalendarRef}>
                  <H4>Using Calendars</H4>
                  <div className="space-y-4">
                    <p>
                      In many cases you want the time-phasing to take into
                      account calendars, so that weekends, holidays and periods
                      of reduced work-hours can be defined.
                    </p>
                    <p>In illustration below we have introduced 2 calendars:</p>
                    <ul className="list-disc px-8">
                      <li>Calendar 1: 7.5 hours every day</li>
                      <li>
                        Calendar 2: 10.0 hours mon-thu, 5.0 hours fridays and
                        weekends off
                      </li>
                    </ul>
                    <p>
                      NOTE: If calendar contains no hours for days within
                      activity range, spread the hours evenly on all days
                    </p>
                    <img src={Image3} alt="timePhasing" />
                  </div>
                </div>

                <div>
                  <H4>Using Profiles</H4>
                  <div className="space-y-4">
                    <div>
                      <p>
                        In some cases, especially for activities with a large
                        amount of hours and long duration, the workload may not
                        be linear. Perhaps you want less hours at start and end
                        of activity and a higher volume in the "middle"?
                      </p>
                      <p>
                        This could've been solved by splitting one activity into
                        several activities with their own start/finish dates and
                        hours. A much easier solution is to use a profile that
                        outlines the "shape" of the workload over time.
                      </p>
                    </div>
                    <p>
                      Example below shows an activity with 140 hours using a
                      "center loaded" profile.
                    </p>
                    <img src={Image4} alt="timePhasing" />
                  </div>
                </div>

                <div>
                  <H4>Points in Time returned</H4>
                  <div className="space-y-4">
                    <p>
                      In many cases you don't want to see the result on a
                      day-by-day basis, but instead summarize the values on
                      different points in time. Points in Time = Time-unit of
                      returned time-phasing result (days, weeks, months,
                      quarters, years)
                    </p>
                    <p>
                      For information: Regardless of points in time to be
                      returned, the time-phasing engine must analyze each item
                      day-by-day.
                    </p>
                    <p>
                      In illustration below we have specified the points in time
                      to be "weeks":
                    </p>
                    <img src={Image5} alt="timePhasing" />
                  </div>
                </div>

                <div>
                  <H4>Grouping</H4>
                  <div className="space-y-4">
                    <div>
                      <p>
                        To avoid transferring a lot of periodic values,
                        aggregation/grouping of activities is supported by the
                        time-phasing engine.
                      </p>
                      <p>
                        By specifying groups and group fields, you will receive
                        a grouped set of data for each individual group or set
                        of groups.
                      </p>
                    </div>
                    <img src={Image6} alt="timePhasing" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <H2>Return Range</H2>
          <div className="space-y-4">
            <div>
              <p>
                In some cases you only want values within a selected time-range.
              </p>
              <p>
                Examples: "from start to today", "from today and into the
                future", "one specific year", ...
              </p>
              <p>In illustration below, one week is selected:</p>
            </div>
            <img src={Image7} alt="timePhasing" />
            <div>
              <H4>Return Value Types</H4>
              <div>
                <p>Perhaps you want a load in return instead of man-hours?</p>
                <p>
                  For example; you want to know how many people you need to get
                  the job done between provided dates.
                </p>
                <p>
                  The time-phasing engine can provide qty (man-hours in this
                  example) or load.
                </p>
                <p>
                  Specify which value you want, and the time-phasing service
                  will deliver accordingly.
                </p>
                <p>Value types: PerQty or PerLoad</p>
              </div>
              <img src={Image8} className="mt-4" alt="timePhasing" />
            </div>
            <div>
              <H4>Input Value Types</H4>
              <div>
                <p>
                  What if you don't know the total man-hours (quantity) but
                  wants this to be calculated?
                </p>
                <p>
                  For example; you may have a set of positions with start/finish
                  dates, load % and calendars.
                </p>
                <p>
                  Another example; you have a group of people available for work
                  between two dates and you want their capacity calculated as
                  number of hours
                </p>
                <p className="mt-4">
                  Inform the time-phasing engine if you are providing load or
                  quantity and the timePhasing service will calculate the
                  corresponding value based on calendars.
                </p>
                <p className="mt-4">Input Value Types: Qty or Load</p>
              </div>
              <img src={Image9} className="mt-4" alt="timePhasing" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
